import { useMemo } from 'react'

import {
  matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { PublicationInspectionModel, PublicationInspectionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['publicationInspections']

export type PublicationInspectionsFilters = {
  brand_id?: number,
  publication_edition_id?: number,
  publication_id?: number,
  status_id?: number,
  string?: string,
}

type UsePublicationInspectionsOptions = {
  filters?: PublicationInspectionsFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationInspectionRequestOptions,
}

function usePublicationInspections(options: UsePublicationInspectionsOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    publication_edition_id: filterPublicationEditionId,
    publication_id: filterPublicationId,
    status_id: filterStatusId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { publicationInspections: publicationInspectionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    agencies, campaigns, publicationInspections,
  } = useSelector(reduxState => reduxState.entities)

  const filteredPublicationInspections = useMemo(() => {
    const filtered = Object.values(publicationInspections).filter((inspection: PublicationInspectionModel) => {
      const {
        campaign_id, publication_edition_id, publication_id, status_id,
      } = inspection

      const campaign = campaigns[campaign_id] || {}
      const agency = agencies[campaign.agency_id] || {}

      const matchBrand = matchFilterNumber(agency.brand_id, filterBrandId)
      const matchPublication = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationEdition = matchFilterNumber(publication_edition_id, filterPublicationEditionId)
      const matchStatus = matchFilterNumber(status_id, filterStatusId)
      const matchString = matchFilterString(campaign.title, filterString)

      return matchBrand && matchPublication && matchPublicationEdition && matchStatus && matchString
    })

    return sortArrayBy(filtered, 'asc', 'id')
  }, [publicationInspectionsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationInspectionsCount = filteredPublicationInspections.length
  const hasFilteredPublicationInspections = !!filteredPublicationInspectionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationInspectionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublicationInspections } = useReduxAction(
    'publicationInspections',
    'loadPublicationInspections',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationInspections,
    filteredPublicationInspectionsCount,
    hasFilteredPublicationInspections,
    loading: loadingPublicationInspections,
  }
}

export default usePublicationInspections
